import { GoldBtn } from '@/components/ui';
import { assets, C, externalLinks } from '@/theme/tokens';
// import { scrollToReserve } from '@/utils/scroll';
import { useIsMobile } from '@/hooks/isMobile';

const HERO_HEADLINE = {
  pre: 'A New Era of Adventure',
  main: 'The Tabletop RPG\n Played IRL',
  sub: 'QuestXR fuses a BRP Game Engine, Augmented Reality, Multi-media, and a suite of GM and Homebrew tools, to create live Quests at real-world locations.\nThis is Tabletop RPG — Embodied.',
} as const;

const SOCIAL_PROOF: ReadonlyArray<{ n: string; label: string }> = [
  { n: 'Playable', label: 'Play Free Now' },
  { n: 'BackerKit', label: 'Crowdfunding platform' },
  { n: '$5.00', label: 'To reserve your spot' },
];

/** The hero section: headline, sub-copy, dual CTAs, social-proof bar, BRP badge. */
export function Hero() {
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '100px 24px 80px',
        background: `linear-gradient(to bottom, rgba(10,26,42,0.8) 20%, rgba(10,26,42,1) 100%), url('/assets/cityview-2.png') center/cover no-repeat`,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background star field — purely decorative */}
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: 'absolute',
            width: i % 5 === 0 ? 2 : 1,
            height: i % 5 === 0 ? 2 : 1,
            borderRadius: '50%',
            background: `rgba(212,175,55,${0.1 + (i % 4) * 0.1})`,
            top: `${5 + ((i * 31) % 90)}%`,
            left: `${(i * 47) % 100}%`,
            animation: `pulse-glow ${3 + (i % 4)}s ${i * 0.3}s ease-in-out infinite`,
          }}
        />
      ))}

      <div style={{ maxWidth: 780, position: 'relative', zIndex: 1 }}>
        <p
          className="hero-animate section-label"
          style={{ justifyContent: 'center', display: 'flex' }}
        >
          {HERO_HEADLINE.pre}
        </p>

        <h1
          className="hero-animate-2 hero-headline"
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 'clamp(2.2rem, 6vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            letterSpacing: '2px',
            color: '#fff',
            textTransform: 'uppercase',
            marginBottom: 24,
            whiteSpace: 'pre-line',
            textShadow: '0 0 60px rgba(212,175,55,0.15)',
          }}
        >
          {HERO_HEADLINE.main.split('\n').map((line, i) => (
            <span key={i} style={{ display: 'block', color: i === 0 ? '#fff' : C.gold }}>
              {line}
            </span>
          ))}
        </h1>

        <p
          className="hero-animate-3"
          style={{
            fontSize: 17,
            fontWeight: 300,
            color: 'rgba(255,255,255,0.75)',
            maxWidth: 600,
            margin: '0 auto 40px',
            lineHeight: 1.7,
          }}
        >
          {HERO_HEADLINE.sub.split('\n').map((line, i) => (
            <span key={i} style={{ display: 'block'}}>
              {line}
            </span>
          ))}
        </p>

        <div
          className="hero-animate-4"
          style={{ display: 'block', justifyContent: 'center', maxWidth: 380, margin: '0 auto' }}
        >
         <GoldBtn
          size="lg"
          href={externalLinks.demo}
          style={{ display: 'block', marginBottom: 20, height: 60, alignItems: 'center', lineHeight: 1.1  }}
          >
            Play a Free Quest Now<br />
            <span style={{ fontSize: 12 }}>(No account required/Play Anywhere)</span>
          </GoldBtn>
          <GoldBtn size="lg"
          variant="outlined"
          href={externalLinks.backerKit}
          style={{ display: 'flex', height: 60, alignItems: 'center', paddingLeft: 6, paddingRight: 6  }}
          >
            ✦ Reserve your spot on BackerKit ✦
          </GoldBtn>
         </div>

        {/* Social proof bar */}
        <div
          style={{
            marginTop: 56,
            padding: '16px 32px',
            display: 'inline-flex',
            gap: 40,
            flexWrap: 'wrap',
            background: `linear-gradient(to bottom, rgba(10,26,42,0.5) 20%, rgba(10,26,42,1) 100%)`,
            justifyContent: 'center',
            borderTop: '1px solid rgba(193,154,107,0.3)',
            borderBottom: '1px solid rgba(193,154,107,0.3)',
          }}
        >
          {SOCIAL_PROOF.map(({ n, label }) => (
            <div key={n} style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: "'Cinzel',serif",
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.gold,
                }}
              >
                {n}
              </div>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: '1.5px',
                  color: C.muted,
                  textTransform: 'uppercase',
                  marginTop: 2,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* BRP compatibility badge */}
        <div
          className="hero-animate-4"
          style={{
            marginTop: 28,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            background: 'rgba(20,38,59,0.8)',
            border: '1px solid rgba(193,154,107,0.4)',
            borderRadius: 5,
            padding: '10px 20px',
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#D4AF37,#C19A6B)',
              display: isMobile ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: "'Cinzel',serif",
              fontSize: 14,
              fontWeight: 900,
              color: '#0a1a2a',
              flexShrink: 0,
            }}
          >
            <img src={assets.brpicon} alt="QuestXR" style={{ height: 36, objectFit: 'contain' }} />
          </div>
            <div
            style={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#D4AF37,#C19A6B)',
              display: isMobile ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: "'Cinzel',serif",
              fontSize: 14,
              fontWeight: 900,
              color: '#0a1a2a',
              flexShrink: 0,
            }}
          >
            <img src={assets.brpicon} alt="QuestXR" style={{ height: 36, objectFit: 'contain' }} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div
              style={{
                fontFamily: "'Cinzel',serif",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '1.5px',
                color: C.gold,
                textTransform: 'uppercase',
              }}
            >
              Based on Chaosium's Basic Roleplaying System
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              Compatible with the same rules that power Call of Cthulhu & RuneQuest...to start.
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          color: C.dim,
          fontSize: 11,
          letterSpacing: 2,
          textTransform: 'uppercase',
          animation: 'floatUp 1s 1.2s ease both',
        }}
      >
        <span>Discover</span>
        <div
          style={{
            width: 1,
            height: 32,
            background: `linear-gradient(to bottom, ${C.bronze}, transparent)`,
          }}
        />
      </div>
    </section>
  );
}
